import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { LogOut, Coins, LogIn, BellRing, Trophy } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import AuthModal from "@/components/AuthModal";

export default function UserMenu() {
  const { user, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  if (loading) return null;

  if (!user) {
    return (
      <>
        <button
          data-testid="google-signin-btn"
          onClick={() => setAuthOpen(true)}
          className="flex items-center gap-2 border border-[#333] bg-[#121212] hover:border-[#cfff04] transition-colors font-mono text-[11px] uppercase tracking-wider text-[#efefef] px-4 py-2.5"
        >
          <LogIn size={13} />
          Sign in
        </button>
        <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
      </>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="user-menu-btn"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 border border-[#333] bg-[#121212] hover:border-[#cfff04] transition-colors px-3 py-1.5"
      >
        {user.picture ? (
          <img src={user.picture} alt={user.name || "avatar"} className="w-6 h-6 rounded-full" referrerPolicy="no-referrer" />
        ) : (
          <span className="w-6 h-6 rounded-full bg-[#cfff04] text-[#050505] flex items-center justify-center font-bold text-xs">
            {(user.name || user.email || "?")[0].toUpperCase()}
          </span>
        )}
        <span className="font-mono text-[11px] uppercase tracking-wider text-[#efefef] max-w-[100px] truncate" data-testid="user-menu-name">
          {(user.name || user.email).split(" ")[0]}
        </span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-52 border border-[#333] bg-[#121212] z-50" data-testid="user-menu-dropdown">
          <div className="px-4 py-3 border-b border-[#3a3a3a]">
            <div className="text-xs text-[#efefef] truncate">{user.name}</div>
            <div className="font-mono text-[11px] text-[#e2e2e2] truncate" data-testid="user-menu-email">{user.email}</div>
          </div>
          <Link
            to="/my-coins"
            data-testid="user-menu-mycoins-link"
            onClick={() => setOpen(false)}
            className="w-full flex items-center gap-2 px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-[#e2e2e2] hover:text-[#cfff04] transition-colors border-b border-[#3a3a3a]"
          >
            <Coins size={13} />
            My Coins
          </Link>
          <Link
            to="/watchlist"
            data-testid="user-menu-watchlist-link"
            onClick={() => setOpen(false)}
            className="w-full flex items-center gap-2 px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-[#e2e2e2] hover:text-[#cfff04] transition-colors border-b border-[#3a3a3a]"
          >
            <BellRing size={13} />
            Watchlist
          </Link>
          <Link
            to="/leaderboard"
            data-testid="user-menu-leaderboard-link"
            onClick={() => setOpen(false)}
            className="w-full flex items-center gap-2 px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-[#e2e2e2] hover:text-[#cfff04] transition-colors border-b border-[#3a3a3a]"
          >
            <Trophy size={13} />
            Leaderboard
          </Link>
          <button
            data-testid="signout-btn"
            onClick={() => {
              setOpen(false);
              logout();
            }}
            className="w-full flex items-center gap-2 px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-[#e2e2e2] hover:text-[#cfff04] transition-colors"
          >
            <LogOut size={13} />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
